import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { getEmployeeDirectory, DirectoryEntry } from '@/store/slices/employeeSlice';
import { createConversation } from '@/store/slices/chatSlice';
import { MessagesStackParamList } from '@/types/navigation';
import { colors, font, space, radius } from '@/theme/chalkCourt';

const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

type Nav = StackNavigationProp<MessagesStackParamList, 'Directory'>;

/**
 * Staff directory — look up any colleague and tap to start a direct message.
 * Sourced from `/employee/directory` (PII-minimized: name + department only).
 */
const DirectoryScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<Nav>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { directory, isLoadingDirectory } = useSelector((state: RootState) => state.employee);
  const myEmployeeId = user?.employee?.id;

  const [query, setQuery] = useState('');
  const [creatingId, setCreatingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getEmployeeDirectory());
  }, [dispatch]);

  useEffect(() => {
    if (!error) return undefined;
    const id = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(id);
  }, [error]);

  const people = useMemo(() => {
    const q = query.trim().toLowerCase();
    return directory
      .filter((p) => p.id !== myEmployeeId)
      .filter(
        (p) =>
          !q ||
          p.fullName.toLowerCase().includes(q) ||
          (p.departmentName ?? '').toLowerCase().includes(q)
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [directory, query, myEmployeeId]);

  const openChat = async (person: DirectoryEntry) => {
    if (creatingId) return;
    setCreatingId(person.id);
    const result = await dispatch(
      createConversation({ type: 'DIRECT', memberEmployeeIds: [person.id] })
    );
    setCreatingId(null);
    if (createConversation.fulfilled.match(result)) {
      navigation.navigate('Conversation', {
        twilioSid: result.payload.twilioConversationSid,
        title: person.fullName,
      });
    } else {
      setError((result.payload as string) || `Couldn't start a chat with ${person.fullName}.`);
    }
  };

  const renderRow = ({ item }: { item: DirectoryEntry }) => {
    const busy = creatingId === item.id;
    return (
      <TouchableOpacity
        style={styles.row}
        activeOpacity={0.7}
        disabled={!!creatingId}
        onPress={() => openChat(item)}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{initials(item.fullName)}</Text>
        </View>
        <View style={styles.rowBody}>
          <Text style={styles.rowName}>{item.fullName}</Text>
          {!!item.departmentName && <Text style={styles.rowDept}>{item.departmentName}</Text>}
        </View>
        {item.admin && (
          <View style={styles.adminPill}>
            <Text style={styles.adminPillText}>ADMIN</Text>
          </View>
        )}
        {busy ? (
          <ActivityIndicator color={colors.accent} style={styles.rowIcon} />
        ) : (
          <Icon
            name="chatbubble-ellipses-outline"
            size={18}
            color={colors.inkFaint}
            style={styles.rowIcon}
          />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.screen}>
      {error && (
        <View style={styles.errorBanner}>
          <Icon name="alert-circle" size={16} color="#c5221f" />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
      <View style={styles.searchWrap}>
        <Icon name="search" size={16} color={colors.inkFaint} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search staff"
          placeholderTextColor={colors.inkFaint}
          value={query}
          onChangeText={setQuery}
          autoCorrect={false}
        />
        {query.length > 0 && (
          <TouchableOpacity onPress={() => setQuery('')}>
            <Icon name="close-circle" size={16} color={colors.inkFaint} />
          </TouchableOpacity>
        )}
      </View>

      {isLoadingDirectory && directory.length === 0 ? (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.accent} />
        </View>
      ) : (
        <FlatList
          data={people}
          keyExtractor={(p) => String(p.id)}
          renderItem={renderRow}
          contentContainerStyle={styles.listContent}
          ListEmptyComponent={
            <View style={styles.centered}>
              <Text style={styles.emptyText}>
                {query ? 'No staff match your search.' : 'No staff found.'}
              </Text>
            </View>
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fce8e6',
    borderWidth: 1,
    borderColor: '#f3c1bd',
    borderRadius: radius.md,
    marginHorizontal: space.lg,
    marginTop: space.md,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  errorText: {
    flex: 1,
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: '#c5221f',
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.surface,
    marginHorizontal: space.lg,
    marginTop: space.md,
    paddingHorizontal: space.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 10,
    fontFamily: font.body,
    fontSize: 15,
    color: colors.ink,
  },
  listContent: {
    padding: space.lg,
    paddingTop: space.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: radius.pill,
    backgroundColor: colors.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: space.md,
  },
  avatarText: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.accentDark,
  },
  rowBody: {
    flex: 1,
  },
  rowName: {
    fontFamily: font.bodySemi,
    fontSize: 15,
    color: colors.ink,
  },
  rowDept: {
    fontFamily: font.body,
    fontSize: 13,
    color: colors.inkFaint,
    marginTop: 2,
  },
  adminPill: {
    backgroundColor: colors.courtSoft,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: radius.pill,
    marginRight: space.sm,
  },
  adminPillText: {
    fontFamily: font.bodySemi,
    fontSize: 10,
    letterSpacing: 0.5,
    color: colors.court,
  },
  rowIcon: {
    marginLeft: 4,
  },
  centered: {
    paddingVertical: space.xxl,
    alignItems: 'center',
  },
  emptyText: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkFaint,
  },
});

export default DirectoryScreen;
